import React, { useRef, useId, useCallback } from "react";
import { MODAL_SEVERITY } from "utils/constants";
import { OctagonAlertIcon, XIcon, TriangleAlertIcon, CircleCheckIcon, InfoIcon } from "lucide-react";
import { Button, ButtonType } from "components/common/Button.component";
import { ICON_SIZE } from "components/common/constants";
import { useFocusTrap } from "components/modals/useFocusTrap";

const TAB_INDEX = 1000;

const SEVERITY_ICONS: Record<string, React.ReactElement> = {
    [MODAL_SEVERITY.DANGER]: <OctagonAlertIcon size={48} />,
    [MODAL_SEVERITY.WARNING]: <TriangleAlertIcon size={48} />,
    [MODAL_SEVERITY.SUCCESS]: <CircleCheckIcon size={48} />,
    [MODAL_SEVERITY.INFORMATION]: <InfoIcon size={48} />,
};

export interface ModalRenderProps {
    loopFocus: () => void;
    initialFocusRef: (element: HTMLElement | null) => void;
}

export interface ModalShellProps {
    open: boolean;
    onClose: () => void;
    label?: string;
    title?: string;
    description?: string;
    severity?: string | null;
    size?: string;
    children: React.ReactNode | ((props: ModalRenderProps) => React.ReactNode);
}

export const Modal = ({
    open,
    onClose,
    label,
    title,
    description,
    severity = null,
    size = "medium",
    children,
}: ModalShellProps): React.ReactElement => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const initialFocusRef = useRef<HTMLElement | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const descriptionId = useId();

    const loopFocus = useCallback(() => {
        closeButtonRef.current?.focus();
    }, []);

    const setInitialFocus = useCallback((element: HTMLElement | null) => {
        initialFocusRef.current = element;
    }, []);

    useFocusTrap(modalRef, {
        isActive: open,
        onEscape: onClose,
        initialFocusRef,
        fallbackFocusRef: closeButtonRef,
    });

    if (!open) return <></>;

    return (
        <>
            <div className="ga-modal__backdrop" />
            <div
                className="ga-modal__container"
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-describedby={description ? descriptionId : undefined}
                ref={modalRef}
            >
                <div className={`ga-modal ga-modal--${size} ${severity ? `ga-modal--${severity}` : ""}`}>
                    <div className="ga-modal__top-section">
                        {severity && (
                            <div className="ga-modal__icon">
                                {SEVERITY_ICONS[severity] ?? null}
                            </div>
                        )}
                        <div className="ga-modal__heading">
                            {label && <div className="ga-modal__label">{label}</div>}
                            {title && <div className="ga-modal__title" id={titleId}>{title}</div>}
                            {description && <div className="ga-modal__description" id={descriptionId}>{description}</div>}
                        </div>
                        <Button
                            type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<XIcon size={ICON_SIZE.MEDIUM} />}
                            dataToggle="modal"
                            dataTarget="#dialogModal"
                            ariaLabel="Close"
                            tabIndex={TAB_INDEX + 1}
                            ref={closeButtonRef}
                            onClick={onClose}
                        />
                    </div>
                    {typeof children === "function" ? children({ loopFocus, initialFocusRef: setInitialFocus }) : children}
                </div>
            </div>
        </>
    );
};

export default Modal;
